import React from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Link,
  Chip,
  Stack,
} from "@mui/material";
import { useCart } from "../../context/CartContext";
import CartItemCard from "../../pages/Cart/CartItemCard";
import { Link as RouterLink } from "react-router-dom";

const money = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    Number.isFinite(n) ? n : 0
  );

const percentLabel = (rate) => {
  const pct = rate * 100;
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(2)}%`;
};

/**
 * Props:
 *  - deliveryFee?: number (optional, non-taxed add-on here; we can extend later with taxable fees)
 *  - headerExtras?: ReactNode (optional slot for badges/chips)
 */
const SummarySidebar = ({ deliveryFee = 0, headerExtras = null }) => {
  const {
    discountedCartItems,
    totalDiscount,
    appliedCoupon,
    summary, // { subtotal, youSaved, taxRate, taxAmount, total }
    TAX_RATE,
  } = useCart();

  const totalWithDelivery = (summary?.total ?? 0) + (deliveryFee || 0);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Order Summary
        </Typography>
        <Box>{headerExtras}</Box>
      </Stack>

      {appliedCoupon ? (
        <Chip
          size="small"
          color="success"
          label={`Coupon applied: ${appliedCoupon}`}
          sx={{ mb: 1 }}
        />
      ) : null}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ maxHeight: 360, overflowY: "auto", mb: 2 }}>
        {discountedCartItems.map((item, idx) => (
          <CartItemCard
            key={idx}
            cartItem={item}
            variant="checkout"
            onDelete={undefined}
            onQuantityChange={undefined}
          />
        ))}
      </Box>

      <Box textAlign="right" mb={2}>
        <Link
          component={RouterLink}
          to="/cart"
          underline="hover"
          variant="body2"
          color="primary"
          fontWeight={500}>
          Edit your cart
        </Link>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Subtotal BEFORE discounts */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>Subtotal</Typography>
        <Typography fontWeight="bold">{money(summary?.subtotal)}</Typography>
      </Box>

      {/* Savings (shown as negative) */}
      {totalDiscount > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            color: "success.main",
          }}>
          <Typography>You saved</Typography>
          <Typography fontWeight="bold">
            - {money(summary?.youSaved)}
          </Typography>
        </Box>
      )}

      {/* Delivery (optional, non-taxed here) */}
      {deliveryFee > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Delivery</Typography>
          <Typography fontWeight="bold">{money(deliveryFee)}</Typography>
        </Box>
      )}

      {/* Tax on discounted items */}
      {summary?.taxAmount === 0 ? (
        <Box
          sx={{
            backgroundColor: "grey.100",
            color: "text.secondary",
            borderRadius: 1,
            px: 2,
            py: 1,
            my: 1.5,
            fontSize: 13,
          }}>
          No tax applicable on selected items
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Tax ({percentLabel(TAX_RATE)})</Typography>
          <Typography fontWeight="bold">{money(summary?.taxAmount)}</Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Total = (Subtotal - Discount + Tax) + Delivery */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>Total</Typography>
        <Typography fontWeight="bold">{money(totalWithDelivery)}</Typography>
      </Box>
    </Paper>
  );
};

export default SummarySidebar;
